import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DateFormattingService, skipIfNotVisible, SystemMetadataService } from '@twaice-fe/frontend/shared/services';
import { actions, selectors } from '@twaice-fe/frontend/shared/store';
import { getTimezoneOffsetFromUtc } from '@twaice-fe/frontend/shared/utilities';
import { MOBILITY_BASE_ROUTE, MOBILITY_DEPRECATED_BASE_ROUTE } from '@twaice-fe/shared/constants';
import { BessMetadata, SystemDetails } from '@twaice-fe/shared/models';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BehaviorSubject, combineLatest, interval, Observable, of, startWith, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ChatDrawerService } from '../chat/chat-drawer.service';
import { NavigationMenuService } from './navigation-menu.service';
import { ConnectionStatus } from './navigation-menu.types';
import { StorageMetadataDialogComponent } from './storage-metadata-dialog/storage-metadata-dialog.component';

const { systemSelectors, energyAnalyticsSelectors } = selectors;
const { energyAnalyticsActions, systemActions } = actions;

@Component({
  selector: 'twaice-fe-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  standalone: false,
})
export class NavigationMenuComponent implements OnInit, OnDestroy {
  @Input() isFleet = false;
  @Input() isGlobal = false;
  @Input() isPerformanceManagerSolution = false;
  @Input() isWarrantyTracker = false;
  @Input() isDataExplorerSolution = false;
  @Input() isPenaltyRiskSolution = false;
  @Input() isAlertsSolution = false;
  @Input() isBMSAlertsSolution = false;
  intercomTarget = 'navigation-menu';

  // eslint-disable-next-line @typescript-eslint/naming-convention
  TOTAL_STORAGE_COUNT = 10;
  storages$: Observable<SystemDetails[]>;
  filteredStorages$: Observable<SystemDetails[]>;
  selectedSystem: SystemDetails;

  selectedStorage$ = new BehaviorSubject<SystemDetails>(undefined);
  systemIngestionStatisticsLastSeenIsLoading$ = new Observable<boolean>();
  lastSeenTimestamp$: Observable<Date>;
  connectionStatus$: Observable<ConnectionStatus>;

  inputValue?: string;

  getTimezoneOffsetFromUtc = getTimezoneOffsetFromUtc;
  now$ = new BehaviorSubject<Date>(new Date());

  private readonly lastSeenPollingFrequencySeconds = 1 * 60;
  private searchTerms = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private modal: NzModalService,
    private chatDrawerService: ChatDrawerService,
    private systemMetadataService: SystemMetadataService,
    private navigationMenuService: NavigationMenuService,
    public dateFormattingService: DateFormattingService
  ) {
    this.initComponentState();
    this.setupSearchSubscription();
    this.setupLastSeenSubscription();
    this.lastSeenTimestamp$ = this.store.select(energyAnalyticsSelectors.getSystemIngestionStatisticsLastSeenTimestamp);
    this.connectionStatus$ = this.setupConnectionStatus();
  }

  ngOnInit(): void {
    this.store
      .select(systemSelectors.getSelected)
      .pipe(
        filter((selected) => !!selected?.id),
        distinctUntilChanged((prev, curr) => prev?.customerBk === curr?.customerBk && prev?.systemBk === curr?.systemBk),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedSystem) => {
        this.selectedSystem = selectedSystem;
        this.setStorages();
      });

    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.now$.next(new Date());
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getStorageLocation(bessMetadata: BessMetadata | null): string {
    return this.systemMetadataService.getStorageLocation(bessMetadata);
  }

  async onRowSelected(item: SystemDetails) {
    this.store.dispatch(systemActions.selectSystem({ systemId: item.id }));

    this.selectedSystem = item;
    this.setSelectedStorage();
  }

  openMetadataDialog() {
    this.modal.create({
      nzContent: StorageMetadataDialogComponent,
      nzData: {
        bessMetadata: this.selectedStorage$.getValue().bessMetadata,
        componentCountOnLevels: this.selectedStorage$.getValue().componentCountOnLevels,
        topology: this.selectedStorage$.getValue().topology,
      },
      nzClosable: false,
      nzFooter: null,
    });
  }

  openChatDrawer() {
    this.chatDrawerService.openChatDrawer({ customerBk: this.selectedSystem.customerBk, systemBk: this.selectedSystem.systemBk });
  }

  handleSearchInput(event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerms.next(value);
  }

  fetchSystemIngestionStatistics(): void {
    this.store.dispatch(energyAnalyticsActions.fetchSystemIngestionStatisticsData());
  }

  isOnDataExplorerLanding(): boolean {
    return this.router.url.split('?')[0].endsWith('/data-explorer/views');
  }

  private setupConnectionStatus(): Observable<ConnectionStatus> {
    return combineLatest([this.lastSeenTimestamp$, this.selectedStorage$.asObservable(), this.now$.asObservable()]).pipe(
      map(([lastSeenTimestamp, selectedStorage, now]) =>
        this.navigationMenuService.getConnectionStatus(lastSeenTimestamp, selectedStorage, now)
      ),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    );
  }

  private setupSearchSubscription() {
    this.searchTerms
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term === '') {
            return this.storages$;
          }

          return this.storages$.pipe(
            map((storages) => storages.filter((storage) => storage.systemName.toLowerCase().includes(term.toLowerCase())))
          );
        }),
        tap((filteredStorages) => {
          this.filteredStorages$ = of(filteredStorages);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private setupLastSeenSubscription() {
    this.systemIngestionStatisticsLastSeenIsLoading$ = this.store
      .select(energyAnalyticsSelectors.getSystemIngestionStatisticsLastSeenLoadingState)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$));

    combineLatest([
      this.selectedStorage$.asObservable(),
      interval(this.lastSeenPollingFrequencySeconds * 1000).pipe(startWith(0)),
    ])
      .pipe(skipIfNotVisible(), takeUntil(this.destroy$))
      .subscribe(() => this.store.dispatch(energyAnalyticsActions.fetchSystemIngestionStatisticsData()));
  }

  private setStorages() {
    this.storages$ = this.store
      .select(systemSelectors.getSystemDetailsList)
      .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)));

    this.storages$
      .pipe(
        take(1),
        tap(() => {
          this.setSelectedStorage();
        })
      )
      .subscribe();

    this.filteredStorages$ = this.storages$;
  }

  private setSelectedStorage() {
    if (!this.selectedSystem) return;

    this.store
      .select(systemSelectors.getSelected)
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedStorage) => this.selectedStorage$.next(selectedStorage));
  }

  private initComponentState() {
    this.isFleet = this.router.url.includes(MOBILITY_BASE_ROUTE) || this.router.url.includes(MOBILITY_DEPRECATED_BASE_ROUTE);
  }
}
